import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import {
  Input,
  Select
} from "src/library/components/Forms";
import { Icon } from "src/library/components/Icon";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { useEventListener } from "src/library/hooks";
import { Icons } from "src/library/components/Icon/types";
import NotificationReveal from "src/core/notifications/components/NotificationReveal";
import { useNotificationsApi } from "src/core/notifications/api/notifications.api";
import type { IStore } from "src/core/shared/store";
import useDrop from "src/core/drops/hooks/useDrop";
import {
  PaginatedParams,
  FilterParams
} from "src/core/shared/api";

import { INotification } from "../api/notifications.api";

interface IProps {
  onClose: () => void;
  terminology: ITerminology;
}

const defaultPagination: PaginatedParams<INotification> = {
  offset: 0,
  limit: 10,
  page: 0,
  sort: [ [ "createdAt", "DESC" ] ]
};

const NotificationsFlyOut: React.ReactFCWithChildren<IProps> = ({ onClose, terminology }) => {
  const [ getNotificationsResponse, getNotifications ] = useNotificationsApi("GET_USER_NOTIFICATIONS");
  const [ markAsReadResponse, markAsRead ] = useNotificationsApi("MARK_NOTIFICATION_AS_READ");

  const { user, customer } = useMappedState(({
    global, customers, notifications
  }: IStore) => ({
    user: global.user,
    customer: customers.customer,
    notifications
  }));

  const [ { drop } ] = useDrop();
  const NotificationsFlyOutRef = React.useRef<HTMLDivElement>(null);
  const [ timerCount ] = React.useState<NodeJS.Timeout | null>();
  const timer = React.useRef(timerCount);
  const [ pagination, setPagination ] = React.useState<PaginatedParams<INotification>>(defaultPagination);
  const [ filters, setFilters ] = React.useState<FilterParams<INotification>>({});
  const [ notifications, setNotifications ] = React.useState<INotification[]>([]);

  const handleGetNotifications = React.useCallback(() => {
    if (user) {
      getNotifications({
        pagination,
        filters,
        userId: user.id
      });
    }
  }, [

    getNotifications,
    filters,
    pagination,
    user
  ]);

  React.useEffect(() => {
    handleGetNotifications();
  }, [ handleGetNotifications ]);

  const handleMarkAsRead = (notificationId: number) => {
    if (user) {
      markAsRead({
        notificationId,
        userId: user.id
      });
    }
  };

  React.useEffect(() => {
    if (getNotificationsResponse.data?.notifications.result) {
      setNotifications(currentNotifications => _.uniqBy([ ...currentNotifications, ...(getNotificationsResponse.data?.notifications.result || []) ], "id"));
    }
  }, [ getNotificationsResponse ]);

  React.useEffect(() => {
    if (markAsReadResponse.data) {
      setNotifications(currentNotifications => _.map(currentNotifications, notification =>
        markAsReadResponse.data?.notificationId === notification.id ?
          {
            ...notification,
            notificationUsers: [ markAsReadResponse.data ]
          } :
          notification
      ));
    }
  }, [ markAsReadResponse ]);

  const clickHandler = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (NotificationsFlyOutRef.current && !NotificationsFlyOutRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [ onClose ]
  );

  useEventListener("mousedown", clickHandler, document);

  const handleSelectChange = React.useCallback((value: string) => {
    if (value === "" && customer && drop) {
      setPagination(currPagination =>
        ({
          ...currPagination,
          page: 0,
          offset: 0
        })
      );

      setFilters(currFilters =>
        ({
          ...currFilters,
          notificationRecipients: {}
        })
      );
    } else {
      setPagination(currPagination =>
        ({
          ...currPagination,
          page: 0,
          offset: 0
        })
      );

      setFilters(currFilters =>
        ({
          ...currFilters,
          notificationRecipients: JSON.parse(value)
        })
      );
    }
  }, [ customer, drop ]);

  const handleSearchChange = React.useCallback((value: string) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      if (!value.length) {
        setPagination(currPagination =>
          ({
            ...currPagination,
            page: 0,
            offset: 0
          })
        );
        setFilters(currFilters => ({ notificationRecipients: currFilters.notificationRecipients }));
      } else {
        setPagination(currPagination =>
          ({
            ...currPagination,
            page: 0,
            offset: 0
          })
        );

        setFilters(currFilters =>
          ({
            ...currFilters,
            $or: [ { title: { $like: `%${value}%` } }, { body: { $like: `%${value}%` } } ]
          })
        );
      }
    }, 300);
  }, []);

  return (
    <div ref={NotificationsFlyOutRef}>
      <NotificationsFlyOutWrapper direction="vertical">
        <Grid.Box className="flyout-title-wrapper">
          <Text fontSize="h2">
            {terminology.notification.plural.text}
          </Text>

          <Icon
            onClick={() => onClose()}
            icon={Icons.CLOSE}
            $fillColor="primaryLight"
            className="close-icon"
          />
        </Grid.Box>

        <Grid.Bounds className="filters-wrapper">
          {drop && customer && (
            <Grid.Box
              className="select-wrapper"
              shiftLeft
            >
              <Select
                as="select"
                name="select-search-notifications"
                autoHideLabels
                label="Select Search Notifications"
                handleChange={e => handleSelectChange(e.target.value)}
                unselectedOption={{
                  text: `All ${terminology.notification.plural.text}`,
                  value: ""
                }}
                options={[
                  {
                    text: `Current ${terminology.drop.singular.text} ${terminology.notification.plural.text}`,
                    value: JSON.stringify({
                      type: "branch",
                      entity_id: drop.id
                    })
                  },
                  {
                    text: `Current ${terminology.customer.singular.text} ${terminology.notification.plural.text}`,
                    value: JSON.stringify({
                      type: "customer",
                      entity_id: customer.id
                    })
                  }
                ]}
              />
            </Grid.Box>
          )}

          <Grid.Box
            shiftRight
            className="search-wrapper"
          >
            <Input
              as="input"
              name="search-notifications"
              autoHideLabels
              label="Search Notifications"
              placeholder={`Search ${terminology.notification.plural.text}...`}
              handleChange={e => {
                handleSearchChange(e.target.value);
              }}
            />
          </Grid.Box>
        </Grid.Bounds>

        {getNotificationsResponse.pending && getNotificationsResponse.data?.notifications.result.length === 0 && (
          <Grid.Box className="loading-wrapper">
            <LoadingSpinner
              color="primaryDark"
              className="loading"
              type="flat"
              position="inline"
            />
          </Grid.Box>
        )}

        {!getNotificationsResponse.pending && getNotificationsResponse.data?.notifications.result.length === 0 && (
          <Grid.Box className="not-found">
            <Text>
              No notifications found!
            </Text>
          </Grid.Box>
        )}

        {getNotificationsResponse.error && (
          <Grid.Box className="error">
            <Text>
              There was an error whilst trying to get your notifications. Please try again.
            </Text>
          </Grid.Box>
        )}

        {_.map(notifications, note => (
          <NotificationReveal
            key={`notification-${note.id}`}
            notification={note}
            markAsRead={id => handleMarkAsRead(id)}
          />
        ))}

        {getNotificationsResponse.pending && notifications.length > 0 && (
          <Grid.Box className="loading-wrapper">
            <LoadingSpinner
              color="primaryDark"
              className="loading"
              type="flat"
              position="inline"
            />
          </Grid.Box>
        )}

        {getNotificationsResponse.data && getNotificationsResponse.data?.notifications.pages > pagination.page + 1 && (
          <Grid.Bounds
            className="show-more"
            direction="vertical"
            halign="center"
            onClick={() => {
              setPagination({
                ...pagination,
                page: pagination.page + 1,
                offset: pagination.limit * (pagination.page + 1)
              });
            }}
          >
            <Text weight="bold">
              Show More
            </Text>

            <Icon
              icon={Icons.CHEVRONDOWN}
              $fillColor="primaryLight"
            />
          </Grid.Bounds>
        )}
      </NotificationsFlyOutWrapper>
    </div>
  );
};

export default NotificationsFlyOut;

const NotificationsFlyOutWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    height: 100vh;
    overflow-y: scroll;
    width: 500px;
    max-width: 100vw;
    left: 300px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    @media (max-width: 1024px) {
      left: 0px;
    }
    background: ${theme.colors.white};
    position: absolute;
    z-index: 500;

    .flyout-title-wrapper {
      height: 70px;
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.md};
      border-bottom: 1px solid ${theme.colors.lightGrey};
      position: relative;
    }

    .filters-wrapper {
      padding: ${theme.spacing.padding.md};
      .search-wrapper {
        width: 40%;
      }

      .select-wrapper {
        width: 40%;
      }
    }

    .close-icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: ${theme.spacing.padding.sm};
    }

    .loading-wrapper {
      padding: ${theme.spacing.padding.sm};
      justify-content: center;
      display: flex;

      .loading {
        margin: auto;
      }
    }

    .not-found,
    .error {
      padding: ${theme.spacing.padding.sm};
      text-align: center;
    }

    .error {
      background: ${theme.colors.errorLight};
    }

    .show-more {
      &:hover {
        cursor: pointer;
        opacity: 0.8;
      }
    }
  `}
`;
